/**
 * Grouping helpers for raw-code odds maps.
 *
 * The Sports API returns each threshold / handicap line as its own market
 * code (`OVER_GOALS_2_5`, `OVER_GOALS_2_25`, `ASIAN_HANDICAP_neg0_5`,
 * `HT_OVER_GOALS_1_5`, `OVER_CORNERS_9_5`, …). The match page's OddsTab
 * shows one card per line, so we group by prefix + line number.
 */
import type { BookmakerOdds, MarketOddsMap } from "./types";

/**
 * Parse the numeric line encoded in a market code's suffix.
 *
 * Handles all the shape drift the Sportal365 feed exposes:
 *   - integer whole numbers:                `OVER_GOALS_5`         → 5
 *   - `_5`, `_25`, `_75` fractional lines:  `OVER_GOALS_2_5`       → 2.5
 *   - lowercase `neg` prefix:               `ASIAN_HANDICAP_neg0_5` → -0.5
 *   - uppercase `NEG_` prefix:              `ASIAN_HANDICAP_NEG_0_5` → -0.5
 *
 * Returns `null` when the code doesn't match the prefix or the suffix isn't parseable.
 */
export function parseMarketLine(code: string, prefix: string): number | null {
    if (!code.startsWith(prefix)) return null;
    let rest = code.slice(prefix.length);
    let negative = false;
    // `neg` (lowercase, no separator) or `NEG_` (uppercase, underscore separator).
    if (/^(neg|NEG_)/.test(rest)) {
        negative = true;
        rest = rest.replace(/^(neg|NEG_)/, "");
    }
    if (rest.length === 0) return null;
    const [whole, frac] = rest.split("_");
    const w = Number.parseInt(whole, 10);
    if (Number.isNaN(w)) return null;
    let n = w;
    if (frac !== undefined) {
        const f = Number.parseInt(frac, 10);
        if (Number.isNaN(f)) return null;
        // "5" → 0.5, "25" → 0.25, "75" → 0.75. Decimal places = frac length.
        n += f / 10 ** frac.length;
    }
    return negative ? -n : n;
}

/**
 * Collect every bookmaker entry from codes matching `prefix`, keyed by the
 * numeric line the code encodes. Returns entries in ascending line order.
 *
 * Example: `collectMarketsByLine(oddsMap, "OVER_GOALS_")` returns
 * `Map<number, BookmakerOdds[]>` with keys 0.5, 1, 1.5, 2, 2.25, 2.5, …
 * — one entry per line, each carrying every operator that quoted it.
 */
export function collectMarketsByLine(oddsMap: MarketOddsMap, prefix: string): Map<number, BookmakerOdds[]> {
    const groups = new Map<number, BookmakerOdds[]>();
    for (const [code, bookies] of oddsMap.entries()) {
        const line = parseMarketLine(code, prefix);
        if (line === null) continue;
        const existing = groups.get(line) ?? [];
        existing.push(...bookies);
        groups.set(line, existing);
    }
    // Sort ascending by line so the UI renders +0.5, +1, +1.5, …
    return new Map([...groups.entries()].sort(([a], [b]) => a - b));
}
